import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  Autocomplete,
  Avatar,
  Box,
  Checkbox,
  Chip,
  Link,
  ListItem,
  ListItemAvatar,
  ListItemText,
  Paper,
  Stack,
  Typography,
  useTheme,
} from "@mui/material";
import { EGButton } from "../../../../../../MainComponents/Button/EGButton/EGButton";
import { EGBasicSelect } from "../../../../../../MainComponents/Select/EGBasicSelect";
import { IconHeader } from "../../../../../../Icon/IconHader/IconHeader";
import { EGIconButton } from "../../../../../../MainComponents/Button/IconButton/IconButton";
import { useReportContext } from "../../../../../ReportContext/useReportContext";
import { EGInput } from "../../../../../../MainComponents/Input/EGInput";
import { useParams } from "react-router";
import {
  createSanctions,
  createCompensations,
  catalogByPart,
} from "../../../../../../../apis";
import { useSnackbar } from "notistack";
import EGDatePicker from "../../../../../../MainComponents/DatePicker/EGDatePicker";

export const SanctionCompensationForm = ({
  closeForm,
  type,
  people,
  reloadInfo,
  reportRender,
}) => {
  // Custom Hooks
  const { id } = useParams();
  const { enqueueSnackbar, closeSnackbar } = useSnackbar();
  const { sanctionsOptions, benefitOptions, involvedType, titlesType } =
    useReportContext();
  const theme = useTheme();
  const [t] = useTranslation("report");
  const [warning] = useTranslation("statuscode");

  // Initial States
  const [isLoading, setIsLoading] = useState(false);
  // Person selected
  const [involvedSelected, setInvolvedSelected] = useState("");
  // Selected actions
  const [selectedActions, setSelectedActions] = useState([]);
  // Add another action
  const [another, setAnother] = useState([]);
  // Sanction date 
  const [determinationDate, setDeterminationDate] = useState(null);
  // Execution date
  const [executionDate, setExecutionDate] = useState(null);
  // Add comment
  const [addComment, setAddComment] = useState(false);
  // General comment about sanction
  const [comment, setComment] = useState("");
  // Catálogos
  const [disciplinaryMeasureOpts, setDisciplinaryMeasureOpts] = useState([]);
  const [otherRecommendationsOpts, setOtherRecommendationsOpts] = useState([]);
  // Otras recomendaciones seleccionadas
  const [selectedOtherRecommendations, setSelectedOtherRecommendations] = useState([]);
  // The form type is determined
  const property =
    type === "offenders" ? "another_sanction" : "another_benefit";

  // Clean form
  const resetForm = () => {
    setInvolvedSelected("");
    setSelectedActions([]);
    setAnother([]);
    setDeterminationDate(null);
    setExecutionDate(null);
    setAddComment(false);
    setComment("");
    setSelectedOtherRecommendations([]);
  };

  // Close & clean selected form
  const hdlCancel = () => {
    if (involvedSelected) {
      const confirmCancel = window.confirm(
        warning("you_have_unsaved_changes_are_you_sure_you_want_to_leave")
      );
      if (!confirmCancel) return;
    }
    resetForm();
    closeForm();
  };

  // Add another sanction
  const hdlAddAnotherAction = () => {
    setTimeout(
      () =>
        window.scrollTo({
          top: document.documentElement.scrollHeight,
          behavior: "smooth",
        }),
      100
    );
    setAnother((prev) => [...prev, { [property]: "" }]);
  };

  // Add another action
  const hdlInputAnotherActionChange = (index, value) => {
    setAnother((prev) =>
      prev.map((item, i) =>
        i === index ? { ...item, [property]: value } : item
      )
    );
  };

  // Delete another action
  const hdlDeleteAnotherAction = (index) => {
    setAnother(another.filter((_, i) => i !== index));
  };

  // Validation data
  const hasEmptyAnother = () =>
    another.some((item) => item[property]?.trim() === "");

  const isInvalid =
    !involvedSelected ||
    (!selectedActions.length && !another.length) ||
    hasEmptyAnother();

  const isAnotherOptionInvalid = hasEmptyAnother();

  // Cargar catálogos
  useEffect(() => {
    const loadCatalogs = async () => {
      try {
        // Cargar catálogo disciplinaryMeasure
        const disciplinaryMeasureResp = await catalogByPart({
          is_own: true,
          catalogue: "disciplinaryMeasure",
          path: "/",
        });
        if (disciplinaryMeasureResp?.data && Array.isArray(disciplinaryMeasureResp.data)) {
          const formattedOptions = disciplinaryMeasureResp.data.map((item) => ({
            value: item.key,
            label: item.label,
          }));
          setDisciplinaryMeasureOpts(formattedOptions);
        }

        // Cargar catálogo otherRecommendations
        const otherRecommendationsResp = await catalogByPart({
          is_own: true,
          catalogue: "otherRecommendations",
          path: "/",
        });
        if (otherRecommendationsResp?.data && Array.isArray(otherRecommendationsResp.data)) {
          const formattedOptions = otherRecommendationsResp.data.map((item) => ({
            value: item.key,
            label: item.label,
          }));
          setOtherRecommendationsOpts(formattedOptions);
        }
      } catch (error) {
        console.error("Error loading catalogs:", error);
      }
    };

    loadCatalogs();
  }, []);

  // Select involved party
  const hdlChangePersonSelected = (e) => {
    const selectedPerson = people.find(
      (option) => option.value === e.target.value
    );
    setInvolvedSelected(selectedPerson);
    setTimeout(() => {
      window.scrollTo({
        top: document.documentElement.scrollHeight,
        behavior: "smooth",
      });
    }, 100);
  };

  // Options type
  const OPTIONS_MAP = {
    offenders: disciplinaryMeasureOpts.length > 0 ? disciplinaryMeasureOpts : sanctionsOptions,
    victims: benefitOptions,
  };
  const options = OPTIONS_MAP[type] || [];

  // Sets the value in an array []
  const hdlActionsChange = (_, newValue) => {
    setSelectedActions(newValue.map((option) => option.value));
  };

  // Send info
  const onSubmitActions = async () => {
    setIsLoading(true);
    const { data } = involvedSelected;
    const sending = enqueueSnackbar(`${t("Resolution.sending")}...`, {
      variant: "info",
      persist: true,
    });

    const PAYLOAD_KEYS = {
      offenders: "sanctions",
      victims: "benefits",
    };

    const API_CALLS = {
      offenders: createSanctions,
      victims: createCompensations,
    };

    const key = PAYLOAD_KEYS[type] || null;
    const endpoint = API_CALLS[type] || null;

    // Crear mapeo de value -> label para sanctions/benefits
    const actionsLabel = {};
    if (selectedActions.length > 0) {
      // Usar las mismas opciones que se usan en el formulario
      const options = type === "offenders"
        ? (disciplinaryMeasureOpts.length > 0 ? disciplinaryMeasureOpts : sanctionsOptions)
        : benefitOptions;
      selectedActions.forEach(actionValue => {
        const found = options.find(opt => opt.value === actionValue);
        if (found) {
          actionsLabel[actionValue] = found.label;
        }
      });
    }

    // Crear mapeo de value -> label para other_recommendations
    const otherRecommendationsLabel = {};
    if (selectedOtherRecommendations.length > 0) {
      selectedOtherRecommendations.forEach(recValue => {
        const found = otherRecommendationsOpts.find(opt => opt.value === recValue);
        if (found) {
          otherRecommendationsLabel[recValue] = found.label;
        }
      });
    }

    const dataToSend = {
      report_identifier: id,
      ...data,
      ...(selectedActions.length > 0 && key ? {
        [key]: selectedActions,
        [`${key}_label`]: actionsLabel
      } : {}),
      ...(another.length > 0 && { another }),
      ...(selectedOtherRecommendations.length > 0 && {
        other_recommendations: selectedOtherRecommendations,
        other_recommendations_label: otherRecommendationsLabel
      }),
      comment,
      determination_date: determinationDate ? determinationDate.format("YYYY-MM-DD") : null,
      execution_date: executionDate ? executionDate.format("YYYY-MM-DD") : null,
    };

    if (!endpoint) {
      console.error("No API function available for type:", type);
      closeSnackbar(sending);
      setIsLoading(false);
      return;
    }

    try {
      await endpoint(dataToSend).then((resp) => {
        if (resp.status === 201) {
          reloadInfo();
          closeForm();
          setInvolvedSelected("");
          setSelectedActions([]);
          setAnother([]);
          setDeterminationDate(null);
          setExecutionDate(null);
          setAddComment(false);
          setComment("");
          setSelectedOtherRecommendations([]);
          setIsLoading(false);
          closeSnackbar(sending);
          reportRender();
          enqueueSnackbar(`${t("Resolution.data_successfully_updated")}`, {
            variant: "success",
          });
        } else {
          closeSnackbar(sending);
          setIsLoading(false);
        }
      });
    } catch (error) {
      console.error(error);
      closeSnackbar(sending);
      setIsLoading(false);
    }
  };

  return (
    <Box>
      <IconHeader title={titlesType[type].title} sx={{ mb: 1.5 }} />
      <EGBasicSelect
        label={`${titlesType[type].selectLabel}:`}
        options={people}
        value={involvedSelected?.value || ""}
        onChange={hdlChangePersonSelected}
        sx={{ mt: 1 }}
      />

      {/* Info involved party selected */}
      {involvedSelected.value !== undefined && (
        <>
          <Paper
            sx={{
              my: 1.5,
              boxShadow: `0 0 4px 0 ${theme.palette.blueGray500.main}`,
              width: "100%",
              background: theme.palette.background.paper,
            }}
            elevation={0}
          >
            <ListItem dense>
              <ListItemAvatar>
                <Avatar sx={{ backgroundColor: "transparent" }}>
                  <EGIconButton
                    iconName={
                      involvedSelected.data.confirm
                        ? "outline_profile_tick"
                        : "outline_profile_remove"
                    }
                    color={
                      involvedSelected.data.confirm
                        ? "limePunch"
                        : "blueGray500"
                    }
                    title={
                      involvedSelected.data.confirm
                        ? t("Resolution.confirmed")
                        : t("Resolution.unconfirmed")
                    }
                    colorHover={
                      involvedSelected.data.confirm ? "greenII" : "blueGray700"
                    }
                  />
                </Avatar>
              </ListItemAvatar>
              <ListItemText
                primary={
                  <Typography variant="h6" textAlign="center">
                    {involvedSelected.label}
                  </Typography>
                }
                secondary={
                  <Stack
                    spacing={1}
                    display="flex"
                    alignItems="center"
                    justifyContent="space-around"
                    flexDirection="row"
                    flexWrap="wrap"
                    py={0.5}
                    component={"span"}
                  >
                    <Typography variant="body2" component="span">
                      {
                        involvedType[
                          involvedSelected.data.kind ||
                            involvedSelected.data.rol
                        ]
                      }
                    </Typography>
                    {involvedSelected.data.additional && (
                      <Typography variant="body2" component="span">{`${t(
                        "Resolution.employee_number"
                      )}: ${involvedSelected.data.additional}`}</Typography>
                    )}
                    {involvedSelected.data.relationship && (
                      <Typography variant="body2" component="span">{`${t(
                        "Resolution.job_position"
                      )}: ${involvedSelected.data.relationship}`}</Typography>
                    )}
                    {involvedSelected.data.details && (
                      <Typography variant="body2" component="span">{`${t(
                        "Resolution.area"
                      )}: ${involvedSelected.data.details}`}</Typography>
                    )}
                  </Stack>
                }
              />
            </ListItem>
          </Paper>
          <Autocomplete
            multiple
            id={`assign-${type}`}
            options={options}
            disableCloseOnSelect
            getOptionLabel={(option) => option.label}
            sx={{ width: "100%", mx: "auto", mt: 1.5 }}
            value={options.filter((option) =>
              selectedActions.includes(option.value)
            )}
            onChange={hdlActionsChange}
            renderTags={(value, getTagProps) =>
              value.map((option, index) => (
                <Chip
                  sx={{
                    backgroundColor: theme.palette.blueGray100.main,
                    fontSize: 12,
                  }}
                  {...getTagProps({ index })}
                  label={
                    <Stack
                      display="flex"
                      alignItems="center"
                      justifyContent="space-between"
                      flexDirection="row"
                      flexWrap="nowrap"
                    >
                      {option.label}
                    </Stack>
                  }
                />
              ))
            }
            isOptionEqualToValue={(option, value) =>
              option.value === value.value
            }
            renderOption={(props, option, { selected }) => {
              return (
                <Box
                  {...props}
                  key={option.value}
                  sx={{
                    maxHeight: 47,
                    p: 2,
                    my: 0.5,
                    mx: 1,
                    borderRadius: 1.5,
                    border: `${theme.palette.blueGray100.main} 1px solid`,
                  }}
                >
                  <Checkbox size="small" checked={selected} />
                  <Typography variant="body2">{option.label}</Typography>
                </Box>
              );
            }}
            renderInput={(params) => (
              <EGInput
                {...params}
                label={`${titlesType[type].inputLabel}:`}
                placeholder={titlesType[type].inputPlaceholder}
              />
            )}
          />
          <Box mt={1.5}>
            <EGDatePicker
              label={`${t("Resolution.determination_date")}:`}
              value={determinationDate}
              onChange={(date) => setDeterminationDate(date)}>
            </EGDatePicker>
          </Box>
          <Box mt={1.5}>
            <EGDatePicker
              label={`${t("Resolution.execution_date")}:`}
              value={executionDate}
              onChange={(date) => setExecutionDate(date)}>
            </EGDatePicker>
          </Box>
          {/* Otras recomendaciones */}
          <Autocomplete
            multiple
            id="other-recommendations"
            options={otherRecommendationsOpts}
            disableCloseOnSelect
            getOptionLabel={(option) => option.label}
            sx={{ width: "100%", mx: "auto", mt: 1.5 }}
            value={otherRecommendationsOpts.filter((option) =>
              selectedOtherRecommendations.includes(option.value)
            )}
            onChange={(_, newValue) => {
              setSelectedOtherRecommendations(newValue.map((option) => option.value));
            }}
            renderTags={(value, getTagProps) =>
              value.map((option, index) => (
                <Chip
                  sx={{
                    backgroundColor: theme.palette.blueGray100.main,
                    fontSize: 12,
                  }}
                  {...getTagProps({ index })}
                  label={
                    <Stack
                      display="flex"
                      alignItems="center"
                      justifyContent="space-between"
                      flexDirection="row"
                      flexWrap="nowrap"
                    >
                      {option.label}
                    </Stack>
                  }
                />
              ))
            }
            isOptionEqualToValue={(option, value) =>
              option.value === value.value
            }
            renderOption={(props, option, { selected }) => {
              return (
                <Box
                  {...props}
                  key={option.value}
                  sx={{
                    maxHeight: 47,
                    p: 2,
                    my: 0.5,
                    mx: 1,
                    borderRadius: 1.5,
                    border: `${theme.palette.blueGray100.main} 1px solid`,
                  }}
                >
                  <Checkbox size="small" checked={selected} />
                  <Typography variant="body2">{option.label}</Typography>
                </Box>
              );
            }}
            renderInput={(params) => (
              <EGInput
                {...params}
                label={`${t("Resolution.other_recommendations")}:`}
                placeholder={t("Resolution.select_other_recommendations")}
              />
            )}
          />
          {/* Another sanctions/remunerations */}
          {another && (
            <Box maxHeight={"25vh"} overflow={"auto"} mt={0.75} py={0.5}>
              {another.map((action, i) => (
                <Stack
                  key={i}
                  display="flex"
                  spacing={0.5}
                  alignItems="start"
                  flexDirection="row"
                  flexWrap="nowrap"
                  mt={1.33}
                >
                  <EGInput
                    label={`${titlesType[type].addActionInput} #${i + 1}:`}
                    placeholder={titlesType[type].addOtherActionPlaceholder}
                    fullWidth
                    value={action[property] || ""}
                    onChange={(e) =>
                      hdlInputAnotherActionChange(i, e.target.value)
                    }
                    required
                    autoFocus
                    helperText={titlesType[type].addOtherActionHelperText}
                  />
                  <EGIconButton
                    iconName="close_icon"
                    iconColor="error"
                    onClick={() => hdlDeleteAnotherAction(i)}
                    title={titlesType[type].deleteAction}
                  />
                </Stack>
              ))}
            </Box>
          )}
          {/* Add another action button */}
          <Stack
            display="flex"
            alignItems="center"
            justifyContent="end"
            flexDirection="row"
            mt={0.5}
            spacing={2}
          >
            <EGIconButton
              iconName="add_square"
              iconColor="primary"
              title={titlesType[type].addAnotherAction}
              onClick={hdlAddAnotherAction}
              disabled={isAnotherOptionInvalid}
            />
            {/* Add comment?  */}
            {(selectedActions.length > 0 || another.length > 0) &&
              !addComment && (
                <Link onClick={() => setAddComment(true)} underline="hover">
                  + {t("Care.add_comment")}
                </Link>
              )}
          </Stack>

          {addComment && (
            <EGInput
              label={`${t("Resolution.details")}: ${comment.length}/500`}
              placeholder={titlesType[type].commentPlaceholder}
              sx={{ mt: 0.5 }}
              multiline
              rows={3}
              value={comment}
              autoFocus
              onChange={(e) => setComment(e.target.value)}
              inputProps={{ maxLength: 500 }}
            />
          )}
        </>
      )}

      <Stack
        display="flex"
        alignItems="center"
        justifyContent="end"
        flexDirection="row"
        flexWrap={{ xs: "wrap", sm: "nowrap" }}
        spacing={2}
        mt={1.5}
        mb={1}
      >
        <EGButton
          label={t("Resolution.save")}
          onClick={onSubmitActions}
          loading={isLoading}
          iconName={isInvalid ? "mark_check" : "mark_check_bold"}
          disabled={isInvalid}
          color={isInvalid ? "light" : "success"}
          iconColor={isInvalid ? "" : "white"}
          iconColorHover={isInvalid ? "" : "white"}
          labelColor={isInvalid ? "" : "white"}
        />
        <EGButton
          iconName="close_icon"
          iconSize={21}
          label={t("Resolution.cancel")}
          onClick={hdlCancel}
          loading={isLoading}
        />
      </Stack>
    </Box>
  );
};
